import { supabase } from '../supabase-client';
import type { Database } from '../types/database';

type AuthWithSession = {
  getSession(): Promise<{ data: { session: { expires_at?: number } | null }; error: Error | null }>;
  refreshSession(): Promise<unknown>;
};

type Profile = Database['public']['Tables']['profiles']['Row'];
type ProfileInsert = Database['public']['Tables']['profiles']['Insert'];
type ProfileUpdate = Database['public']['Tables']['profiles']['Update'];

// Get user profile
export const getProfile = async (userId: string) => {
  const { data, error } = await supabase
    .from('profiles')
    .select('*')
    .eq('id', userId)
    .single();

  if (error) throw error;
  return data as Profile;
};

// Create user profile
export const createProfile = async (profile: ProfileInsert) => {
  const { data, error } = await supabase
    .from('profiles')
    .insert(profile)
    .select()
    .single();

  if (error) throw error;
  return data as Profile;
};

// Update user profile
export const updateProfile = async (userId: string, updates: ProfileUpdate) => {
  const { data, error } = await supabase
    .from('profiles')
    .update({ ...updates, updated_at: new Date().toISOString() })
    .eq('id', userId)
    .select()
    .single();

  if (error) throw error;
  return data as Profile;
};

// Decrement user credits
export const decrementCredits = async (userId: string) => {
  const profile = await getProfile(userId);
  
  if (profile.credits_remaining <= 0 && profile.plan === 'free') {
    throw new Error('No credits remaining');
  }

  if (profile.plan === 'free') {
    const { data, error } = await supabase
      .from('profiles')
      .update({ 
        credits_remaining: profile.credits_remaining - 1,
        updated_at: new Date().toISOString()
      })
      .eq('id', userId)
      .select()
      .single();

    if (error) throw error;
    return data as Profile;
  }

  return profile;
};

// Check if user has credits
export const hasCredits = async (userId: string) => {
  const profile = await getProfile(userId);
  return profile.plan === 'pro' || profile.credits_remaining > 0;
};

// Upgrade to pro
export const upgradeToPro = async (userId: string) => {
  const { data, error } = await supabase
    .from('profiles')
    .update({ 
      plan: 'pro',
      credits_remaining: 999999, // Unlimited for pro
      updated_at: new Date().toISOString()
    })
    .eq('id', userId)
    .select()
    .single();

  if (error) throw error;
  return data as Profile;
};

/**
 * Request account deletion
 * Marks the account as deleted_request = true and is_active = false
 * This blocks user access to their account
 */
export const requestAccountDeletion = async (userId: string): Promise<Profile> => {
  const { data, error } = await supabase
    .from('profiles')
    .update({
      deleted_request: true,
      is_active: false,
      updated_at: new Date().toISOString()
    })
    .eq('id', userId)
    .select()
    .single();

  if (error) {
    console.error('Error requesting account deletion:', error);
    throw error;
  }

  return data as Profile;
};

/**
 * Delete user account permanently via Edge Function.
 * Requires the delete-user function to be deployed.
 * Refreshes session before invoke to avoid 401 with expired tokens.
 */
export const deleteUserAccount = async (): Promise<void> => {
  const auth = supabase.auth as unknown as AuthWithSession;
  const { data: { session }, error: sessionError } = await auth.getSession();
  if (sessionError || !session) {
    throw new Error('No active session. Please sign in again.');
  }
  const expiresAt = session.expires_at;
  if (expiresAt && expiresAt - Math.floor(Date.now() / 1000) < 60) {
    await auth.refreshSession();
  }

  const { data, error } = await supabase.functions.invoke('delete-user', {
    method: 'POST',
    body: {},
  });

  if (error) {
    console.error('Error deleting account:', error);
    throw new Error(error.message || 'Failed to delete account');
  }

  if (data?.error) {
    throw new Error(data.details || data.error);
  }
};

/**
 * Admin: Delete another user's account permanently.
 * Requires the delete-user Edge Function and that the caller is an admin.
 */
export const adminDeleteUserAccount = async (targetUserId: string): Promise<void> => {
  const { data, error } = await supabase.functions.invoke('delete-user', {
    method: 'POST',
    body: { target_user_id: targetUserId },
  });

  if (error) {
    console.error('Error deleting user account:', error);
    throw new Error(error.message || 'Failed to delete user account');
  }

  if (data?.error) {
    throw new Error(data.details || data.error);
  }
};

